//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import * as RadixRadioGroup from '@radix-ui/react-radio-group';
import React from 'react';
import styled, { css } from 'styled-components';

import DescriptionAndValidationWrapper from '@components/molecules/description-and-validation-wrapper/DescriptionAndValidationWrapper';
import { Mixins } from '@theme/mixins';
import { ITrackableComponent, withTracking } from '@utils/withTracking';

interface RadioGroupProps<T extends string>
  extends RadixRadioGroup.RadioGroupProps {
  label?: string;
  instructions?: string;
  value?: T;
  defaultValue?: T;
}

/**
 * A radio group is a component composed by a set of radio buttons where only one can be
 * checked at a time. The entire group can be disabled to prevent user interaction, ensuring the
 * component's state remains consistent with the application's logic when necessary.
 *
 * `RadioGroup` is the root component that contains all the parts of a radio group.
 *
 * `RadioGroup.Item` consists of an item in the group that can be checked. An input will also
 * render when used within a form to ensure events propagate correctly.
 *
 * `RadioGroup.Indicator` renders when the radio item is in a checked state. You can style this
 * element directly, or you can use it as a wrapper to put an icon into, or both.
 *
 * `RadioGroup.Label` renders a label for the radio item. It can be used in two different ways:
 * as a sibling of `RadioGroup.Item` (prop `htmlFor` must be set to the same value as the prop `id`
 * from `RadioGroup.Item`) or as a parent of `RadioGroup.Item` (props `htmlFor` and `id` not
 * needed).
 *
 * @example
 * <RadioGroup
 *     value={value}
 *     onValueChange={(value) => setValue(value)}
 *  >
 *   <RadioGroup.Item id='first-option' value='1'>
 *     <RadioGroup.Indicator />
 *   </RadioGroup.Item>
 *   <RadioGroup.Label htmlFor='first-option'>
 *     First Option
 *   </RadioGroup.Label>
 *   <RadioGroup.Item id='second-option' value='2'>
 *     <RadioGroup.Indicator />
 *   </RadioGroup.Item>
 *   <RadioGroup.Label htmlFor='second-option'>
 *     Second Option
 *   </RadioGroup.Label>
 * </RadioGroup>
 *
 * <RadioGroup
 *    value={value}
 *    onValueChange={(value) => setValue(value)}
 * >
 *   <RadioGroup.Label>
 *     <RadioGroup.Item value='1'>
 *       <RadioGroup.Indicator />
 *     </RadioGroup.Item>
 *     First Option
 *   </RadioGroup.Label>
 *   <RadioGroup.Label>
 *     <RadioGroup.Item value='2'>
 *       <RadioGroup.Indicator />
 *     </RadioGroup.Item>
 *     Second Option
 *   </RadioGroup.Label>
 * </RadioGroup>
 */
const RadioGroupRoot = <T extends string>(props: RadioGroupProps<T>) => (
  <StyledRadioGroupRoot {...props} />
);

export const RadioGroup = <T extends string>({
  instructions,
  label,
  ...props
}: RadioGroupProps<T>) => {
  const renderInput = () => <RadioGroupRoot {...props} />;

  return (
    <StyledDescriptionAndValidationWrapper
      instructions={instructions}
      label={label}
      renderFormComponent={renderInput}
    />
  );
};

interface RadioGroupItemProps<T extends string>
  extends RadixRadioGroup.RadioGroupItemProps {
  value: T;
}

const RadioGroupItem = <T extends string>(
  props: RadioGroupItemProps<T> & ITrackableComponent,
) => <StyledRadioGroupItem {...props} />;

const RadioGroupIndicator = styled(RadixRadioGroup.Indicator)(
  ({ theme }) => css`
    display: flex;
    align-items: center;
    justify-content: center;
    width: ${theme.spacings[2]};
    height: ${theme.spacings[2]};
    background-color: ${theme.colors.aliases.primary};
    border-radius: 50%;

    &[data-disabled] {
      background-color: ${theme.colors.gray4};
    }
  `,
);

const RadioGroupLabel = styled.label(
  () => css`
    ${Mixins.Text.Body}
    cursor: pointer;
    margin: 0;

    :has(${StyledRadioGroupItem}[data-disabled]) {
      cursor: not-allowed;
      ${Mixins.Text.BodyDisabled}
    }
  `,
);

const StyledRadioGroupItem = withTracking(
  styled(RadixRadioGroup.Item)(
    ({ theme }) => css`
      display: inline-flex;
      flex: none;
      align-items: center;
      justify-content: center;
      width: ${theme.spacings[3]};
      height: ${theme.spacings[3]};
      border: 1px solid ${theme.colors.gray2};
      border-radius: 50%;
      padding: ${theme.spacings[0]};
      background-color: ${theme.colors.white};

      &[data-disabled] {
        background-color: ${theme.colors.gray0};
        cursor: not-allowed;
      }

      &[data-disabled] + label {
        cursor: not-allowed;
        ${Mixins.Text.BodyDisabled}
      }

      &:not([data-disabled]) {
        :hover {
          border-color: ${theme.colors.gray7};
        }
      }
    `,
  ),
);

const StyledRadioGroupRoot = styled(RadixRadioGroup.Root)(
  ({ theme }) => css`
    display: flex;
    gap: ${theme.spacings[2]};
  `,
);

const StyledDescriptionAndValidationWrapper = styled(
  DescriptionAndValidationWrapper,
)`
  & > div:first-child {
    margin-bottom: ${({ theme }) => theme.spacings[2]};
  }
`;

RadioGroup.Item = RadioGroupItem;
RadioGroup.Indicator = RadioGroupIndicator;
RadioGroup.Label = RadioGroupLabel;
